// Core
import { useNavigate } from "react-router-dom";

// MUI
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";

// Components
import Logo from "components/Logo";

// Core
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

function Section({ title, children }) {
  return (
    <Box mb={4}>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

function PrivacyPolicy() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation(["privacyPolicy"]);
  const isDark = theme.palette.mode === "dark";

  const bg = isDark ? "#0f1419" : "#f8f9fa";
  const surface = isDark ? "#1a2332" : "#ffffff";
  const textPrimary = isDark ? "#f0f4f8" : "#1a2332";
  const textSecondary = isDark ? "#8a98ad" : "#67748e";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";

  return (
    <Box minHeight="100vh" bgcolor={bg} py={4}>
      {/* Back button */}
      <Box sx={{ position: "fixed", top: 16, left: 16, zIndex: 10 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
            color: textPrimary,
            "&:hover": {
              backgroundColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
            },
          }}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
      </Box>

      <Container maxWidth="md">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            mb: 4,
            pt: 2,
          }}
        >
          <Logo />
          <Typography
            variant="h4"
            fontWeight="bold"
            mt={3}
            mb={1}
            sx={{ color: textPrimary, textAlign: "center" }}
          >
            {t("privacyPolicy:title")}
          </Typography>
          <Typography variant="body2" sx={{ color: textSecondary }}>
            {t("privacyPolicy:lastUpdated")}
          </Typography>
        </Box>

        {/* Card */}
        <Box
          sx={{
            background: surface,
            borderRadius: "16px",
            border: `1px solid ${borderColor}`,
            boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.4)" : "0 4px 24px rgba(0,0,0,0.08)",
            p: { xs: 3, md: 6 },
            color: textPrimary,
          }}
        >
          <Typography variant="body1" mb={4} sx={{ color: textSecondary }}>
            {t("privacyPolicy:intro")}
          </Typography>

          <Divider sx={{ mb: 4, borderColor }} />

          <Section title={t("privacyPolicy:platformObjectives_title")}>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t("privacyPolicy:platformObjectives_content")}
            </Typography>
          </Section>

          <Section title={t("privacyPolicy:purposes_title")}>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t("privacyPolicy:purposes_content")}
            </Typography>
          </Section>

          <Section title={t("privacyPolicy:personalData_title")}>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t("privacyPolicy:personalData_content")}
            </Typography>
          </Section>

          <Section title={t("privacyPolicy:responsible_title")}>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t("privacyPolicy:responsible_content")}
            </Typography>
          </Section>

          <Section title={t("privacyPolicy:retention_title")}>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t("privacyPolicy:retention_content")}
            </Typography>
          </Section>

          <Section title={t("privacyPolicy:sharing_title")}>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t("privacyPolicy:sharing_content")}
            </Typography>
          </Section>

          <Section title={t("privacyPolicy:security_title")}>
            <Typography variant="body2" sx={{ color: textSecondary }} mb={2}>
              {t("privacyPolicy:security_intro")}
            </Typography>
            <Box component="ul" sx={{ pl: 3, color: textSecondary }}>
              {[
                t("privacyPolicy:security_item0"),
                t("privacyPolicy:security_item1"),
                t("privacyPolicy:security_item2"),
                t("privacyPolicy:security_item3"),
              ].map((item) => (
                <Typography key={item} component="li" variant="body2" mb={0.5}>
                  {item}
                </Typography>
              ))}
            </Box>
            <Typography variant="body2" sx={{ color: textSecondary }} mt={2}>
              {t("privacyPolicy:security_outro")}
            </Typography>
          </Section>

          <Section title={t("privacyPolicy:dpo_title")}>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t("privacyPolicy:dpo_intro")}{" "}
              <Box
                component="a"
                href="https://www.inesctec.pt/en"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "#10b981", textDecoration: "none" }}
              >
                inesctec.pt
              </Box>
              .
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }} mt={1}>
              {t("privacyPolicy:dpo_contact")}{" "}
              <Box
                component="a"
                href="mailto:dpo@inesctec.pt"
                sx={{ color: "#10b981", textDecoration: "none" }}
              >
                dpo@inesctec.pt
              </Box>
              .
            </Typography>
          </Section>

          <Section title={t("privacyPolicy:rights_title")}>
            <Typography variant="body2" sx={{ color: textSecondary }} mb={2}>
              {t("privacyPolicy:rights_para1")}
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }} mb={2}>
              {t("privacyPolicy:rights_para2")}
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }} mb={2}>
              {t("privacyPolicy:rights_para3")}
            </Typography>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              {t("privacyPolicy:rights_supervisory_prefix")}
              <Box
                component="a"
                href="https://www.cnpd.pt"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "#10b981", textDecoration: "none" }}
              >
                www.cnpd.pt
              </Box>
              {t("privacyPolicy:rights_supervisory_suffix")}
            </Typography>
          </Section>

          <Section title={t("privacyPolicy:legalBasis_title")}>
            <Typography variant="body2" sx={{ color: textSecondary }}>
              <strong>{t("privacyPolicy:legalBasis_bold")}</strong>{" "}
              {t("privacyPolicy:legalBasis_text")}
            </Typography>
          </Section>

          <Divider sx={{ mb: 3, borderColor }} />

          <Typography variant="body2" sx={{ color: textSecondary, fontStyle: "italic" }}>
            {t("privacyPolicy:footer")}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default PrivacyPolicy;
